/**
 * The `common` namespace some site-builder blocks read (`useTranslation('common')`).
 *
 * <p>Those blocks come from the Akademi portal, which ships its own `common`
 * catalogue. The storefront had none. Every key came back missing, and the
 * blocks fell back to their built-in Turkish: "Form yüklenemedi.", a "Gönder"
 * button, and "Bu alan zorunludur." under each required field, on an
 * English-only shop.
 *
 * <p>Owner, 2026-09-22: no Turkish by default. A customer sees Turkish only
 * when the shop's own locale list includes it. So English is complete here.
 * Turkish is given only for the same keys, and is reached only by a shop whose
 * site descriptor lists a Turkish locale; `BlocksI18nProvider` picks the
 * language from that descriptor.
 *
 * <p>`form.tooShort` and its siblings are looked up without their number (the
 * block passes none), so their wording carries no count.
 *
 * <p>Learning-platform keys (courses, activities, points) are left out: no shop
 * template uses those blocks.
 */
const en = {
  form: {
    loadError: "This form couldn't be loaded. Please refresh the page or try again later.",
    notConfigured: 'No form has been chosen for this block.',
    submit: 'Send',
    success: "Thank you. We've received your message and will be in touch soon.",
    submitError: "Your form couldn't be sent. Please try again.",
    alreadySubmitted: "You've already sent this form.",
    required: 'This field is required.',
    pattern: 'Please enter a valid value.',
    tooShort: 'This is too short.',
    tooLong: 'This is too long.',
    tooSmall: 'This value is too small.',
    tooLarge: 'This value is too large.',
    honeypot: { label: 'Leave this field empty' },
  },
  hero: {
    carousel: {
      previous: 'Previous slide',
      next: 'Next slide',
      selector: 'Choose a slide',
      goToSlide: 'Go to slide {{index}}',
      slidePosition: 'Slide {{index}} of {{total}}',
      primaryBanner: 'Main banner',
      secondaryBanner: 'Second banner',
      categoryBanner: 'Category banner',
    },
  },
  search: { search: 'Search…', noResults: 'No results found' },
  button: {
    cancel: 'Cancel',
    continue: 'Continue',
    delete: 'Delete',
    deleting: 'Deleting…',
    edit: 'Edit',
    saving: 'Saving…',
    seeAll: 'See all',
    showMore: 'Show more',
    update: 'Update',
  },
  label: {
    apply: 'Apply',
    clear: 'Clear',
    close: 'Close',
    description: 'Description',
    error: 'Failed — try again',
    FAQ: 'FAQ',
    hideDetails: 'Hide details',
    loading: 'Loading…',
    moreInfo: 'More info',
    next: 'Next',
    today: 'Today',
  },
  title: {
    faq: 'Frequently asked questions',
    recordNotFound: 'No records found.',
    noResulFound: 'No results found.',
    serviceUnavailableMessage: 'The service is busy. Please try again shortly.',
  },
  message: {
    errorOccuredDuringProcess: 'Something went wrong. Please try again.',
    notFoundCriteria: 'Nothing matched your search.',
  },
};

const tr = {
  form: {
    loadError: 'Form yüklenemedi.',
    notConfigured: 'Form seçilmedi.',
    submit: 'Gönder',
    success: 'Formunuz alınmıştır. Teşekkür ederiz.',
    submitError: 'Form gönderilemedi. Lütfen tekrar deneyin.',
    alreadySubmitted: 'Bu formu zaten doldurdunuz.',
    required: 'Bu alan zorunludur.',
    pattern: 'Geçerli bir değer giriniz.',
    tooShort: 'Değer çok kısa.',
    tooLong: 'Değer çok uzun.',
    tooSmall: 'Değer çok küçük.',
    tooLarge: 'Değer çok büyük.',
    honeypot: { label: 'Bu alanı boş bırakın' },
  },
};

export const locales = [{ ns: 'common', resources: { en, tr } }];
